import { useCallback, useEffect } from 'react';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useFeatures } from 'adapter/in/common/context/Features/data';
import { isSome, isNone, map, Option } from 'fp-ts/Option';
import Preset from 'domain/entity/preset';
import Feature, { Features } from 'domain/entity/feature';
import { WIDGET_INDICATOR, WIDGET_CHART, WIDGET_OTHER, WidgetType, WIDGET_TABULAR } from 'domain/entity/widget';
import { dispatchWidgetMenuView, PANEL, NONE } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import i18n from 'asset/translation';
import { dispatchCurrentWidgetConfiguration, useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { useBranding } from 'adapter/in/common/context/static/Branding';
import Branding from 'domain/entity/branding';
import { ErrorType } from 'domain/types';
import { FeaturePresetCombo, PanelMainData, PanelWidgetType } from 'adapter/in/common/widget/WidgetTypePreset/types';
import {
    sortedCharts,
    sortedIndicators,
    sortedOthers,
    sortedTabulars,
    WidgetTypeFieldGroupItem,
} from 'adapter/in/common/widget/WidgetTypePreset/useWidgetTypePresetContainer';

export const mapWidgetTypesToFieldgroup = (
    availableWidgetTypes: string[],
    selectedWidgetType: WidgetType | NONE,
    isFeaturePresetSelected: boolean
): {
    name: string;
    values: WidgetTypeFieldGroupItem[];
    selectedValue: WidgetType | NONE;
}[] => [
    {
        name: i18n.t('widgets.widgetMenu.indicators'),
        values: sortedIndicators(availableWidgetTypes, isFeaturePresetSelected),
        selectedValue: selectedWidgetType in WIDGET_INDICATOR ? selectedWidgetType : 'NONE',
    },
    {
        name: i18n.t('widgets.widgetMenu.charts'),
        values: sortedCharts(availableWidgetTypes, isFeaturePresetSelected),
        selectedValue: selectedWidgetType in WIDGET_CHART ? selectedWidgetType : 'NONE',
    },
    {
        name: i18n.t('widgets.widgetMenu.tabular'),
        values: sortedTabulars(availableWidgetTypes, isFeaturePresetSelected),
        selectedValue: selectedWidgetType in WIDGET_TABULAR ? selectedWidgetType : 'NONE',
    },
    {
        name: i18n.t('widgets.widgetMenu.others'),
        values: sortedOthers(availableWidgetTypes, isFeaturePresetSelected),
        selectedValue: selectedWidgetType in WIDGET_OTHER ? selectedWidgetType : 'NONE',
    },
];

const usePanelMainContainer = (): {
    data: Option<PanelMainData>;
    onWidgetSelect: (widget: PanelWidgetType) => void;
    onFeaturePresetSelect: (featurePresetCombo: FeaturePresetCombo) => void;
    isLoading: boolean;
    error: ErrorType;
    tryAgain: () => void;
    branding?: Branding;
} => {
    const { profile } = useProfile();
    const { readResult, read, readIsLoading, readError } = useFeatures();
    const { branding } = useBranding();
    const { setPanel } = dispatchWidgetMenuView();
    const {
        widgetType: selectedWidgetType,
        availableWidgetTypes,
        feature: selectedFeature,
        preset: selectedPreset,
    } = useCurrentWidgetConfiguration();
    const { setAvailableWidgetTypes, setFeature, setPreset, setWidgetType } = dispatchCurrentWidgetConfiguration();

    const mapFeaturePresetCombo = (features: Feature[]) =>
        features
            .map((feature: Feature) =>
                feature.presets.map((preset: Preset) => ({
                    text: `${feature.name} - ${preset.name}`,
                    preset: { key: preset.key, name: preset.name },
                    feature: { key: feature.key, name: feature.name },
                    availableWidgetTypes: preset.widgetTypes,
                    isSelected: feature.key === selectedFeature.key && preset.key === selectedPreset.key,
                }))
            )
            .flat();

    const handleOnWidgetSelect = useCallback((widget: PanelWidgetType) => {
        setWidgetType(widget.value);
        setPanel(PANEL.CONFIGURATION);
    }, []);

    const handleFeaturePresetSelect = useCallback((featurePresetCombo: FeaturePresetCombo) => {
        setPreset({ key: featurePresetCombo.preset.key, name: featurePresetCombo.preset.name });
        setFeature({ key: featurePresetCombo.feature.key, name: featurePresetCombo.feature.name });
        setAvailableWidgetTypes(featurePresetCombo.availableWidgetTypes);
    }, []);

    useEffect(() => {
        if (isNone(readResult)) {
            const { locale } = profile;
            read(locale);
        }
    }, [readResult, profile]);

    useEffect(() => {
        if (isSome(readResult) && readResult.value.length > 0) {
            handleFeaturePresetSelect({
                text: '',
                feature: { name: readResult.value[0].name, key: readResult.value[0].key },
                preset: { name: readResult.value[0].presets[0].name, key: readResult.value[0].presets[0].key },
                isSelected: false,
                availableWidgetTypes: readResult.value[0].presets[0].widgetTypes,
            });
        }
    }, [readResult]);

    return {
        data: map((result: Features) => ({
            fieldGroupWidgets: mapWidgetTypesToFieldgroup(
                availableWidgetTypes,
                selectedWidgetType,
                !!(selectedPreset.key !== 'NONE' && selectedFeature.key !== 'NONE')
            ),
            featurePresetCombo: mapFeaturePresetCombo(result),
        }))(readResult),
        onWidgetSelect: handleOnWidgetSelect,
        onFeaturePresetSelect: handleFeaturePresetSelect,
        branding,
        isLoading: readIsLoading,
        error: readError,
        tryAgain: () => {
            read(profile.locale);
        },
    };
};

export default usePanelMainContainer;
